import { FC, useState } from 'react';
import { useSnackbar } from 'notistack';
import { Button, Autocomplete, TextField, Typography, Box } from '@mui/material';
import { Loader, Modal, ModalSaveSection } from '../../../components';
import { IFieldWalkthroughDetail } from '../../../models';
import { generateConsolidatedFloorPlan, getFieldWalkthroughDefaultMarkups } from '../../../fetch';
import { downloadFile, base64StringToBlob } from '../../../helpers';
import { Architecture } from '@mui/icons-material';
import ExpressUtils from '@pdftron/pdfjs-express-utils';

interface IConsolidatedFloorplanButton {
  isFieldWalkthroughLoading: boolean;
  walkthrough: IFieldWalkthroughDetail | null;
  fieldWalkthroughId: string;
}

interface IFloorplanOption {
  name: string;
  displayName: string;
}

export const ConsolidatedFloorplanButton: FC<IConsolidatedFloorplanButton> = ({
  isFieldWalkthroughLoading,
  walkthrough,
  fieldWalkthroughId
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFloorplan, setSelectedFloorplan] = useState<string>('');
  const [hasAnnotations, setHasAnnotations] = useState<boolean>(true);
  const [annotationCheckMessage, setAnnotationCheckMessage] = useState<string>('');

  const checkFloorplanAnnotations = async (floorplanName: string) => {
    if (!floorplanName) {
      setHasAnnotations(true);
      setAnnotationCheckMessage('');
      return;
    }

    try {
      const consolidatedData = await generateConsolidatedFloorPlan(
        fieldWalkthroughId,
        floorplanName
      );

      if (!consolidatedData.xmlAnnotations || consolidatedData.xmlAnnotations.length === 0) {
        setHasAnnotations(false);
        setAnnotationCheckMessage(
          `No annotations found for "${floorplanName.replace(
            /\.pdf$/i,
            ''
          )}". Please select another floorplan.`
        );
      } else {
        setHasAnnotations(true);
        setAnnotationCheckMessage('');
      }
    } catch (error) {
      setHasAnnotations(false);
      setAnnotationCheckMessage('Error checking annotations for this floorplan.');
    }
  };

  // Get available floorplans for dropdown selection
  const getAvailableFloorplans = (): IFloorplanOption[] => {
    if (!walkthrough?.items) return [];

    const floorplanNames = new Set<string>();

    walkthrough.items.forEach(item => {
      item.markupFiles?.forEach(markup => {
        if (markup.originalFileName) {
          floorplanNames.add(markup.originalFileName);
        }
      });
    });

    return Array.from(floorplanNames)
      .map(name => ({
        name,
        displayName: name.replace(/\.pdf$/i, '')
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const handleDownloadConsolidatedFloorplan = async () => {
    try {
      setIsLoading(true);

      // Get all the XML annotation data for this floorplan
      const consolidatedData = await generateConsolidatedFloorPlan(
        fieldWalkthroughId,
        selectedFloorplan
      );

      // Get the base floorplan PDF from default markups
      const defaultMarkups = await getFieldWalkthroughDefaultMarkups(fieldWalkthroughId);
      const floorplanNameWithoutExtension = selectedFloorplan.replace(/\.pdf$/i, '');

      // Try exact filename match first
      let matchingFloorplan = defaultMarkups.find(
        f =>
          f.fileDownloadName.replace(/\.pdf$/i, '').toLowerCase() ===
          floorplanNameWithoutExtension.toLowerCase()
      );

      if (!matchingFloorplan) {
        matchingFloorplan = defaultMarkups.find(
          f =>
            f.description?.replace(/\.pdf$/i, '').toLowerCase() ===
            floorplanNameWithoutExtension.toLowerCase()
        );
      }

      if (!matchingFloorplan) {
        enqueueSnackbar(
          `Base floorplan "${floorplanNameWithoutExtension}" not found. Available: ${defaultMarkups
            .map(f => f.fileDownloadName.replace(/\.pdf$/i, ''))
            .join(', ')}`,
          { variant: 'error' }
        );
        return;
      }

      // Merge XML annotations into a single XFDF string
      const mergedXfdf = mergeXmlAnnotations(
        consolidatedData.xmlAnnotations.map(a => a.xmlAnnotationData)
      );
      if (!mergedXfdf) {
        enqueueSnackbar('No valid annotation data found to merge.', { variant: 'warning' });
        return;
      }

      const pdfBlob = base64StringToBlob(matchingFloorplan.fileContents);

      // Merge and download PDF with annotations
      try {
        const utils = new ExpressUtils();
        const mergeResponse = await utils.setFile(pdfBlob).setXFDF(mergedXfdf).merge();
        const mergedBlob = await mergeResponse.getBlob();
        const fileName = `Consolidated_${floorplanNameWithoutExtension}.pdf`;
        downloadFile(mergedBlob, fileName);

        enqueueSnackbar(`Successfully downloaded consolidated floorplan!`, {
          variant: 'success'
        });

        setShowModal(false);
        setSelectedFloorplan('');
      } catch (mergeError) {
        console.error('Error merging PDF with annotations:', mergeError);
        enqueueSnackbar('Error merging annotations. Please try again.', {
          variant: 'error'
        });
        return;
      }
    } catch (error) {
      console.error('Error creating consolidated floorplan:', error);
      enqueueSnackbar(error?.Detail || 'Error creating consolidated floorplan, please try again.', {
        variant: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Merge multiple XML annotation strings into a single XFDF format
  const mergeXmlAnnotations = (xmlAnnotations: string[]): string => {
    const validAnnotations = xmlAnnotations.filter(xml => xml && xml.trim());
    if (validAnnotations.length === 0) return '';

    if (validAnnotations.length === 1) {
      return validAnnotations[0];
    }

    try {
      const annotationElements: string[] = [];

      validAnnotations.forEach(xmlData => {
        try {
          const parser = new DOMParser();
          const xmlDoc = parser.parseFromString(xmlData, 'text/xml');

          // Extract annotation elements
          const annotElements = xmlDoc.querySelectorAll(
            'square, circle, text, ink, line, polygon, polyline, highlight, underline, strikeout, freetext'
          );

          annotElements.forEach(element => {
            annotationElements.push(element.outerHTML);
          });
        } catch (parseError) {
          console.warn('Failed to parse XML annotation:', parseError);
        }
      });

      if (annotationElements.length === 0) {
        console.warn('No annotation elements found to merge');
        return validAnnotations[0];
      }

      // Create merged XFDF
      return `<?xml version="1.0" encoding="UTF-8"?>
      <xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">
        <annots>
          ${annotationElements.join('\n    ')}
        </annots>
      </xfdf>`;
    } catch (mergeError) {
      console.error('Error merging XML annotations:', mergeError);
      return validAnnotations[0];
    }
  };

  const handleClick = () => {
    setShowModal(true);
    setSelectedFloorplan('');
  };

  const availableFloorplans = getAvailableFloorplans();

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        disabled={isFieldWalkthroughLoading || !walkthrough || availableFloorplans.length === 0}
        startIcon={<Architecture />}
        onClick={handleClick}
        sx={{
          width: {
            xs: '100%',
            sm: 'auto'
          },
          marginBottom: {
            xs: '1rem',
            sm: 0
          }
        }}
      >
        Consolidated Floorplan
      </Button>

      <Modal
        title="Select Consolidated Floorplan"
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedFloorplan('');
        }}
        maxWidth="sm"
      >
        {isLoading && (
          <Loader type="overlay" position="centered" title="Creating consolidated PDF..." />
        )}

        <Box>
          {availableFloorplans.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No floorplan markup files found in this walkthrough.
            </Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" mb={1} mt={0.5}>
                Select a floorplan to download a PDF with all annotations from matching files merged
                together.
              </Typography>

              <Autocomplete<IFloorplanOption>
                options={availableFloorplans}
                getOptionLabel={option => option.displayName}
                value={availableFloorplans.find(fp => fp.name === selectedFloorplan) || null}
                onChange={(event, newValue: IFloorplanOption | null) => {
                  const floorplanName = newValue?.name || '';
                  setSelectedFloorplan(floorplanName);
                  if (floorplanName) {
                    checkFloorplanAnnotations(floorplanName);
                  } else {
                    setHasAnnotations(true);
                    setAnnotationCheckMessage('');
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Floorplan"
                    variant="outlined"
                    placeholder="Select a floorplan..."
                    fullWidth
                  />
                )}
                size="small"
                disabled={isLoading}
              />
              {annotationCheckMessage && (
                <Typography variant="body2" color="warning.main" mt={0.5}>
                  {annotationCheckMessage}
                </Typography>
              )}
            </>
          )}
        </Box>
        <ModalSaveSection
          handleCancel={() => {
            setShowModal(false);
            setSelectedFloorplan('');
            setHasAnnotations(true);
            setAnnotationCheckMessage('');
          }}
          saveButtonText="Download"
          isSaveDisabled={isLoading || !selectedFloorplan || !hasAnnotations}
          handleSave={handleDownloadConsolidatedFloorplan}
        />
      </Modal>
    </>
  );
};
